/**
 * timeline/TimelineMetrics.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Headline KPI strip displayed above the Gantt grid.
 * Each metric card reacts to hover and the "overdue" card pulses when > 0.
 */

import React from 'react';

const METRICS_CONFIG = [
    {
        key: 'total',
        label: 'Total Tasks',
        icon: 'task_alt',
        iconColor: 'text-[#03488A]',
        bgColor: 'bg-[#d5e3ff]/40',
        pulse: false,
    },
    {
        key: 'active',
        label: 'Active',
        icon: 'play_circle',
        iconColor: 'text-[#00AAF1]',
        bgColor: 'bg-[#c9e6ff]/40',
        pulse: false,
    },
    {
        key: 'upcoming',
        label: 'Upcoming',
        icon: 'event_upcoming',
        iconColor: 'text-violet-500',
        bgColor: 'bg-violet-50',
        pulse: false,
    },
    {
        key: 'overdue',
        label: 'Overdue',
        icon: 'event_busy',
        iconColor: 'text-red-500',
        bgColor: 'bg-red-50',
        pulse: true, // will pulse when value > 0
    },
    {
        key: 'underReview',
        label: 'Under Review',
        icon: 'rate_review',
        iconColor: 'text-amber-500',
        bgColor: 'bg-amber-50',
        pulse: false,
    },
    {
        key: 'completed',
        label: 'Completed',
        icon: 'check_circle',
        iconColor: 'text-emerald-500',
        bgColor: 'bg-emerald-50',
        pulse: false,
    },
    {
        key: 'onHold',
        label: 'On Hold',
        icon: 'pause_circle',
        iconColor: 'text-orange-400',
        bgColor: 'bg-orange-50',
        pulse: false,
    },
];

export default function TimelineMetrics({ metrics = {} }) {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {METRICS_CONFIG.map((cfg) => {
                const value = metrics[cfg.key] ?? 0;
                const shouldPulse = cfg.pulse && value > 0;

                return (
                    <div
                        key={cfg.key}
                        className={`
                            ${cfg.bgColor}
                            bg-white dark:bg-[#1e293b]
                            rounded-2xl p-4
                            shadow-[0px_4px_16px_rgba(25,28,29,0.05)]
                            border border-white/60 dark:border-slate-700/60
                            flex flex-col gap-2
                            hover:shadow-[0px_8px_24px_rgba(25,28,29,0.10)]
                            hover:-translate-y-0.5
                            transition-all duration-200
                            cursor-default
                        `}
                    >
                        <div className="flex items-center justify-between">
                            <span
                                className={`material-symbols-outlined text-xl ${cfg.iconColor} ${shouldPulse ? 'animate-pulse' : ''}`}
                                style={{ fontFamily: 'Material Symbols Outlined', fontSize: 20, fontVariationSettings: "'FILL' 1" }}
                                aria-hidden="true"
                            >
                                {cfg.icon}
                            </span>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[#191c1d] dark:text-white font-headline leading-none">
                                {value}
                            </p>
                            <p className="text-xs font-bold text-[#737782] dark:text-slate-400 uppercase tracking-wider mt-1">
                                {cfg.label}
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
